import { useEffect, useState } from "react";
import { TextField, Theme, SxProps } from "@mui/material";
import { createFilterOptions } from "@mui/material/Autocomplete";
import StyledAutocomplete from "components/StyledAutocomplete";
import useDebounce from "helpers/useDebounce";
import { useSnackbar } from "notistack";
import { runGqlQuery } from "gqlClient";
import { GetCaseNamesDocument } from "generated/graphql";
import { extractGQLError } from "shared/PatternGraphQLError";

interface CaseTableFilterEnhacedProps {
  label: string;
  selection: string | undefined;
  setSelection: (s: string | undefined) => void;
  sx?: SxProps<Theme>;
  lensId: number;
}
const CaseTableFilterEnhanced = (props: CaseTableFilterEnhacedProps) => {
  const { label, selection, setSelection, sx, lensId } = props;
  const [prefixQuery, setPrefixQuery] = useState<string>("");
  const prefixQueryDebounced = useDebounce(prefixQuery, 200);
  const [claimants, setClaimants] = useState<string[]>([]);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    if (prefixQueryDebounced && prefixQueryDebounced.length >= 2) {
      runGqlQuery(GetCaseNamesDocument, {
        lensId,
        query: prefixQueryDebounced,
      })
        .then((response) => setClaimants(response.lens.caseNames.claimants))
        .catch((error) => {
          const gql = extractGQLError(error);
          enqueueSnackbar(`Error searching cases: ${gql?.message || "unknown"}`, { variant: "error" });
        });
    }
  }, [prefixQueryDebounced]);

  const options = claimants
    .map((n) => ({ type: "claimant", name: n }))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  return (
    <StyledAutocomplete
      data-pendo="case-home-searchbar"
      sx={sx || {}}
      id="case-filters"
      size="small"
      fullWidth
      autoComplete
      filterOptions={createFilterOptions({
        ignoreCase: true,
        matchFrom: "any",
        stringify: (f) => f.name,
      })}
      autoHighlight
      freeSolo
      value={selection || ""}
      onInputChange={(_evt, v) => {
        setPrefixQuery(v);
      }}
      onChange={(_evt, v) => {
        if (v === null) {
          setSelection(undefined);
        } else if (typeof v === "string") {
          setSelection(v as string);
        } else if (v) {
          setSelection((v as any).name);
        }
      }}
      options={options}
      getOptionLabel={(f) => {
        if (typeof f === "string") {
          return f;
        } else {
          return `${f.name} (${f.type})`;
        }
      }}
      renderInput={(params) => <TextField {...params} label={label} fullWidth />}
    />
  );
};

export default CaseTableFilterEnhanced;
